import { useState, useEffect } from 'react';
import { useServices } from '../services/hooks/useServices';
import type { ServiceWithChecklist } from '../services/types';
import { Checklist, ChecklistItem, ChecklistInstance, JobChecklistInstanceProps } from './types';
import { 
  CheckSquare, 
  Camera, 
  Clock,
  User,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  Play,
  Save
} from 'lucide-react';
import { DateUtils } from '@cleanpro/common-ui';

export default function JobChecklistInstance({ 
  jobId, 
  jobStatus, 
  serviceId,
  serviceName, 
  customerName,
  onUpdateProgress 
}: Readonly<JobChecklistInstanceProps>) {
  const { services } = useServices();
  const [checklistInstance, setChecklistInstance] = useState<ChecklistInstance | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [activeNoteItem, setActiveNoteItem] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  // Get checklist from service data
  const getChecklistForService = (serviceId: string, serviceName: string): Checklist | null => {
    const service = services.find(s => s.id === serviceId) as ServiceWithChecklist | undefined;
    
    if (service?.checklist) {
      // Convert ServiceChecklist to Checklist format
      return {
        id: service.checklist.id,
        name: service.checklist.name,
        description: service.checklist.description,
        serviceType: service.category,
        items: service.checklist.items.map(item => ({
          ...item,
          order: 0,
          allowPhotos: false,
          category: undefined
        })),
        isActive: service.checklist.isActive
      };
    }
    
    // Fallback to default checklist if service doesn't have one
    const serviceKey = serviceName.toLowerCase();
    
    if (serviceKey.includes('deep') || serviceKey.includes('move')) {
      return {
        id: 'deep-cleaning-checklist',
        name: 'Deep Cleaning Checklist',
        description: 'Comprehensive deep cleaning tasks',
        serviceType: 'deep-cleaning',
        isActive: true,
        items: [
          {
            id: 'deep-1',
            text: 'Clean inside of all appliances (oven, refrigerator, dishwasher)',
            required: true,
            allowPhotos: true,
            order: 1,
            category: 'Kitchen'
          },
          {
            id: 'deep-2',
            text: 'Scrub grout and tiles in bathroom',
            required: true,
            allowPhotos: true,
            order: 2,
            category: 'Bathroom'
          },
          {
            id: 'deep-3',
            text: 'Clean light fixtures and ceiling fans',
            required: true,
            allowPhotos: false,
            order: 3,
            category: 'General'
          },
          {
            id: 'deep-4',
            text: 'Wipe down all baseboards and trim',
            required: true,
            allowPhotos: false,
            order: 4,
            category: 'General'
          },
          {
            id: 'deep-5',
            text: 'Clean inside all cabinets and drawers',
            required: true,
            allowPhotos: true,
            order: 5,
            category: 'Kitchen'
          },
          {
            id: 'deep-6',
            text: 'Document any pre-existing damage',
            required: true,
            allowPhotos: true,
            order: 6,
            category: 'Documentation'
          }
        ]
      };
    } else {
      return {
        id: 'standard-cleaning-checklist',
        name: 'Standard Cleaning Checklist',
        description: 'Regular house cleaning tasks',
        serviceType: 'house-cleaning',
        isActive: true,
        items: [
          {
            id: 'std-1',
            text: 'Dust all surfaces in living areas',
            required: true,
            allowPhotos: true,
            order: 1,
            category: 'Living Areas'
          },
          {
            id: 'std-2',
            text: 'Vacuum all carpets and rugs',
            required: true,
            allowPhotos: false,
            order: 2,
            category: 'Living Areas'
          },
          {
            id: 'std-3',
            text: 'Clean and sanitize kitchen countertops',
            required: true,
            allowPhotos: true,
            order: 3,
            category: 'Kitchen'
          },
          {
            id: 'std-4',
            text: 'Clean inside and outside of microwave',
            required: true,
            allowPhotos: false,
            order: 4,
            category: 'Kitchen'
          },
          {
            id: 'std-5',
            text: 'Clean and disinfect bathroom surfaces',
            required: true,
            allowPhotos: true,
            order: 5,
            category: 'Bathroom'
          },
          {
            id: 'std-6',
            text: 'Scrub and sanitize toilet',
            required: true,
            allowPhotos: false,
            order: 6,
            category: 'Bathroom'
          },
          {
            id: 'std-7',
            text: 'Make beds and organize bedrooms',
            required: false,
            allowPhotos: true,
            order: 7,
            category: 'Bedrooms'
          },
          {
            id: 'std-8',
            text: 'Take before and after photos',
            required: true,
            allowPhotos: true,
            order: 8,
            category: 'Documentation'
          }
        ]
      };
    }
  };

  // Initialize checklist instance
  useEffect(() => {
    const serviceChecklist = getChecklistForService(serviceId, serviceName);
    
    if (!serviceChecklist) {
      console.warn(`No checklist found for service ${serviceName} (${serviceId})`);
      return;
    }
    
    setChecklist(serviceChecklist);

    // Create or load checklist instance
    const instance: ChecklistInstance = {
      id: `instance-${jobId}`,
      checklistId: serviceChecklist.id,
      jobId,
      status: jobStatus === 'in-progress' ? 'in-progress' : 'pending',
      completedItems: serviceChecklist.items.map(item => ({
        itemId: item.id,
        completed: false,
        completedAt: undefined,
        completedBy: undefined,
        notes: '',
        photos: []
      })),
      startedAt: jobStatus === 'in-progress' ? new Date().toISOString() : undefined
    };

    setChecklistInstance(instance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, serviceId, serviceName, jobStatus, services]);

  // Update progress callback
  useEffect(() => {
    if (checklistInstance && checklist && onUpdateProgress) {
      const completed = checklistInstance.completedItems.filter(item => item.completed).length;
      const total = checklist.items.length;
      onUpdateProgress({ completed, total });
    }
  }, [checklistInstance, checklist, onUpdateProgress]);

  const toggleItemCompletion = (itemId: string) => {
    if (!checklistInstance) return;

    const updatedItems = checklistInstance.completedItems.map(item => {
      if (item.itemId === itemId) {
        const isCompleting = !item.completed;
        return {
          ...item,
          completed: isCompleting,
          completedAt: isCompleting ? new Date().toISOString() : undefined,
          completedBy: isCompleting ? 'Current User' : undefined
        };
      }
      return item;
    });

    setChecklistInstance({
      ...checklistInstance,
      completedItems: updatedItems
    });
  };

  const updateItemNote = (itemId: string, note: string) => {
    if (!checklistInstance) return;

    const updatedItems = checklistInstance.completedItems.map(item => {
      if (item.itemId === itemId) {
        return { ...item, notes: note };
      }
      return item;
    });

    setChecklistInstance({
      ...checklistInstance,
      completedItems: updatedItems
    });
  };

  const startChecklist = () => {
    if (!checklistInstance) return;

    setChecklistInstance({
      ...checklistInstance,
      status: 'in-progress',
      startedAt: new Date().toISOString()
    });
  };

  const completeChecklist = () => {
    if (!checklistInstance) return;

    setChecklistInstance({
      ...checklistInstance,
      status: 'completed',
      completedAt: new Date().toISOString()
    });
  };

  const groupItemsByCategory = (items: ChecklistItem[]) => {
    const grouped = items.reduce((acc, item) => {
      const category = item.category || 'General';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, ChecklistItem[]>);

    for (const category of Object.keys(grouped)) {
      grouped[category].sort((a, b) => a.order - b.order);
    }

    return grouped;
  };

  const getCompletionStats = () => {
    if (!checklistInstance || !checklist) return { completed: 0, total: 0, percentage: 0 };
    
    const completed = checklistInstance.completedItems.filter(item => item.completed).length;
    const total = checklist.items.length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    
    return { completed, total, percentage };
  };

  const handleSaveNote = () => {
    if (activeNoteItem) {
      updateItemNote(activeNoteItem, noteText);
      setActiveNoteItem(null);
      setNoteText('');
    }
  };

  if (!checklist || !checklistInstance) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <CheckSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Job Checklist</h3>
          <p className="text-gray-500">Preparing checklist for this service...</p>
        </div>
      </div>
    );
  }

  const stats = getCompletionStats();
  const groupedItems = groupItemsByCategory(checklist.items);

  // Helper function to get status badge classes
  const getStatusBadgeClasses = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'in-progress') return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Helper function to get status label
  const getStatusLabel = (status: string) => {
    if (status === 'in-progress') return 'In Progress';
    if (status === 'completed') return 'Completed';
    return 'Pending';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <span>{checklist.name}</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {customerName} • {serviceName}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClasses(checklistInstance.status)}`}>
              {getStatusLabel(checklistInstance.status)}
            </div>
            {checklistInstance.status === 'pending' && (
              <button
                onClick={startChecklist}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Play className="h-4 w-4" />
                <span>Start Checklist</span>
              </button>
            )}
            {checklistInstance.status === 'in-progress' && stats.completed === stats.total && (
              <button
                onClick={completeChecklist}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Complete Job</span>
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{stats.completed}/{stats.total} tasks completed</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.percentage}%` }}
            ></div>
          </div>
        </div>

        {checklistInstance.startedAt && (
          <div className="flex items-center space-x-2 text-sm text-gray-500 mt-2">
            <Clock className="h-4 w-4" />
            <span>Started: {DateUtils.formatDateLong(checklistInstance.startedAt)} at {DateUtils.formatTimeFromDate(checklistInstance.startedAt)}</span>
          </div>
        )}
      </div>

      {/* Checklist Items */}
      <div className="p-6">
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category}>
              <h4 className="font-medium text-gray-700 mb-3 flex items-center space-x-2">
                <span className="text-indigo-600">{category}</span>
                <span className="text-sm text-gray-500">
                  ({items.filter(item => checklistInstance.completedItems.find(ci => ci.itemId === item.id)?.completed).length}/{items.length})
                </span>
              </h4>
              
              <div className="space-y-3">
                {items.map((item) => {
                  const completedItem = checklistInstance.completedItems.find(ci => ci.itemId === item.id);
                  const isCompleted = completedItem?.completed || false;
                  const isEditing = activeNoteItem === item.id;
                  
                  return (
                    <div key={item.id} className={`border rounded-lg p-4 transition-colors ${
                      isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <button
                          onClick={() => toggleItemCompletion(item.id)}
                          disabled={checklistInstance.status === 'completed'}
                          className={`mt-1 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                            isCompleted
                              ? 'bg-green-600 border-green-600 text-white'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {isCompleted && <CheckCircle2 className="h-3 w-3" />}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium ${
                              isCompleted ? 'text-green-800 line-through' : 'text-gray-900'
                            }`}>
                              {item.text}
                            </span>
                            <div className="flex items-center space-x-2">
                              {item.required && (
                                <span className="text-xs text-red-500 font-medium">Required</span>
                              )}
                              {item.allowPhotos && (
                                <Camera className="h-4 w-4 text-blue-500" />
                              )}
                            </div>
                          </div>
                          
                          {isCompleted && completedItem?.completedAt && (
                            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                              <User className="h-3 w-3" />
                              <span>Completed by {completedItem.completedBy} at {DateUtils.formatDateLong(completedItem.completedAt)} {DateUtils.formatTimeFromDate(completedItem.completedAt)}</span>
                            </div>
                          )}
                          
                          {/* Notes Section */}
                          <div className="mt-2">
                            {!isEditing && completedItem?.notes && (
                              <div className="bg-gray-50 rounded p-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                  <span>{completedItem.notes}</span>
                                  <button
                                    onClick={() => {
                                      setActiveNoteItem(item.id);
                                      setNoteText(completedItem.notes || '');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <MessageSquare className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            )}
                            {!isEditing && !completedItem?.notes && (
                              <button
                                onClick={() => setActiveNoteItem(item.id)}
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                              >
                                <MessageSquare className="h-3 w-3" />
                                <span>Add note</span>
                              </button>
                            )}
                            {isEditing && (
                              <div className="space-y-2">
                                <textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Add notes about this task..."
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  rows={2}
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleSaveNote}
                                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 flex items-center space-x-1"
                                  >
                                    <Save className="h-3 w-3" />
                                    <span>Save</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setActiveNoteItem(null);
                                      setNoteText('');
                                    }}
                                    className="text-xs text-gray-600 hover:text-gray-800"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Footer */}
      {checklistInstance.status === 'completed' && (
        <div className="p-6 border-t border-gray-200 bg-green-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-900">Job Completed Successfully</span>
            </div>
            {checklistInstance.completedAt && (
              <span className="text-sm text-green-700">
                Finished: {DateUtils.formatDateLong(checklistInstance.completedAt)} at {DateUtils.formatTimeFromDate(checklistInstance.completedAt)}
              </span>
            )}
          </div>
        </div>
      )}
      
      {checklistInstance.status === 'in-progress' && stats.completed < stats.total && (
        <div className="p-6 border-t border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {stats.total - stats.completed} tasks remaining
              </span>
            </div>
            <span className="text-sm text-blue-700">Complete all required tasks to finish the job</span>
          </div>
        </div>
      )}
    </div>
  );
}
